/**
 * @keystone/bots — OWNER: bots builder (packages/bots/**).
 * Bot brains emit the same Command objects as a human player (PLAN §7):
 *   awareness layer (dodge > kick > defensive > healer triage) preempts a
 *   SimulationCraft-style priority list (APL) per class.
 * Determinism: each bot owns a createSeededRng(seed); no Math.random / Date.
 */
import type { BotBrain, BotView } from '@keystone/sim';
import type { Persona, PersonaMode } from '@keystone/content';
import { createBot } from './brain';
import type { SkillInput } from './skill';

export { createBot, personaSkill, rotationAbilities, THINK_MS, type BotOptions } from './brain';
// +field: solo grind (web Auto)
export { grindStatusOf, type GrindBrain } from './brain';
export { GATHER_BOTH_YD, GATHER_MOB_CLEAR_YD, GATHER_SEEK_YD, type GatherMode } from './gather'; // +prof
export { GRIND_MAX_LEVEL_GAP, GRIND_REST_HP, GRIND_RESTED_HP, PORTAL_MARGIN, nearPortal, type GrindHome, type GrindState, type GrindStatus } from './grind';
// +personas
export { createPersonaBot, AFK_KICK_MS, QUIT_BASE, QUIT_PER_PATIENCE, TILT, TILTED_AT, type PersonaBotOptions, type PersonaBrain, type PersonaIncident, type PersonaState } from './persona/brain';
export { ChatVoice, pickLine, talkiness, SITUATION_CHANCE, MIN_GAP_MS, BURST_MAX, BURST_WINDOW_MS } from './persona/chat';
export { defaultCompanions, isPersonaBrain, socialSummary, type SocialSummary } from './persona/roster';
export { AWAY_WAIT_MS } from './movement';
export { BOT_PRESETS, DEFAULT_PRESET, resolveSkill, witsSkill, type BotPresetName, type SkillInput } from './skill'; // +grid: witsSkill
export { KITS, kitFor, genericKit, type ClassKit, type HealerSpells } from './kits';
export { evalCond, pickChoice, validChoices, type AplChoice, type AplCond, type AplEntry, type AplTarget } from './apl';
export { assignKicks, kickOrder, kickableCasts, type KickAssignment } from './awareness/kick';
export { predictedIncoming, triage } from './awareness/healer';
export { perceive, type Perception } from './perception';

export interface PartyBotOptions {
  /**
   * +personas: the simulated players behind the bots. Each bot slot takes the first unused persona
   * of its class, else the next unused one; slots left over play without a persona. Absent = plain
   * bots, exactly as before.
   */
  personas?: readonly Persona[];
  /** +personas: 'companion' when a human is in the party (default), 'unhinged' for all-bot runs. */
  mode?: PersonaMode;
  /** +personas: see BotOptions.personaSkill. */
  personaSkill?: boolean;
}

/** Bots for every party unit whose controller is 'bot'. */
export function createPartyBots(view: Pick<BotView, 'world'>, skill: SkillInput, seed: number, opts: PartyBotOptions = {}): BotBrain[] {
  const out: BotBrain[] = [];
  const free = [...(opts.personas ?? [])];
  view.world.partyIds.forEach((id, i) => {
    const u = view.world.units[id];
    if (!u || u.controller !== 'bot' || !u.classId) return;
    const at = free.findIndex((p) => p.classId === u.classId);
    const persona = free.length ? free.splice(at >= 0 ? at : 0, 1)[0] : undefined;
    out.push(
      createBot({
        unitId: id,
        classId: u.classId,
        skill,
        seed: seed * 31 + i,
        ...(persona ? { persona, mode: opts.mode ?? 'companion', ...(opts.personaSkill !== undefined ? { personaSkill: opts.personaSkill } : {}) } : {}),
      }),
    );
  });
  return out;
}
